import { inject, Pipe, PipeTransform } from '@angular/core';
import { Charge, ChargePlan } from '../models/paywall-settings';
import { PaywallService } from '../services/paywall.service';
import { periodNoun } from '../helpers/quantity.helper';
import {
  chargeAmount,
  chargePeriodUnitPrice,
  hasOneOffCharges,
  isOneOffCharge,
  planOneTimeTotal,
  planRecurringTotal,
  PricingContext
} from '../helpers/pricing.helper';

/** Two decimals with a thousands separator ("11,700.00"), matching how Stripe Checkout writes amounts. */
const amountFormat = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Formats the recurring total of a charge plan for the selected currency and billing period.
 * The other methods expose the per-charge and one-time amounts a card or summary lists.
 */
@Pipe({
  name: 'price',
  standalone: true,
  pure: false
})
export class PricePipe implements PipeTransform {
  paywallService: PaywallService = inject(PaywallService);

  transform(chargePlan: ChargePlan | undefined, quantity?: number): string | undefined {
    return this.formatAmount(this.recurringTotal(chargePlan, quantity));
  }

  recurringTotal(chargePlan: ChargePlan | undefined, quantity?: number): number {
    return planRecurringTotal(chargePlan, this.quantityOrDefault(chargePlan, quantity), this.context());
  }

  oneTimeTotal(chargePlan: ChargePlan | undefined, quantity?: number): number {
    return planOneTimeTotal(chargePlan, this.quantityOrDefault(chargePlan, quantity), this.context());
  }

  hasOneTime(chargePlan: ChargePlan | undefined): boolean {
    return hasOneOffCharges(chargePlan);
  }

  oneTimeLabel(chargePlan: ChargePlan | undefined, quantity?: number): string {
    return `+ ${this.formatAmount(this.oneTimeTotal(chargePlan, quantity))} one-time`;
  }

  chargeLabel(charge: Charge, quantity: number): string {
    const amount = this.formatAmount(chargeAmount(charge, quantity, this.context()));
    return isOneOffCharge(charge) ? `${amount} one-time` : amount;
  }

  formatAmount(amount: number): string {
    return `${amountFormat.format(amount)} ${this.paywallService.selectedCurrency()?.code || ''}`;
  }

  /** "X CUR / seat / period" for a quantity based charge. */
  unitLabel(charge: Charge | undefined): string {
    const price = amountFormat.format(chargePeriodUnitPrice(charge, this.context()));
    const currency = this.paywallService.selectedCurrency()?.code || '';
    const suffix = isOneOffCharge(charge) ? 'one-time' : periodNoun(this.paywallService.selectedBillingPeriod().value);
    return `${price} ${currency} / seat / ${suffix}`;
  }

  context(): PricingContext {
    return {
      currencyCode: this.paywallService.selectedCurrency()?.code ?? '',
      billingPeriod: this.paywallService.selectedBillingPeriod().value,
      discount: this.paywallService.settings()?.discount
    };
  }

  private quantityOrDefault(chargePlan: ChargePlan | undefined, quantity?: number): number {
    return quantity ?? this.paywallService.quantityFor(chargePlan);
  }
}
